"use client"

import { useSidebar } from "./useSidebar"
import clsx from "clsx"
import { usePathname } from "next/navigation"
import { VerticalNav } from "../Nav"
import { SecondarySidebar } from "./SecondarySidebar"
import { FragmentType, graphql, useFragment } from "@/gql"
import type { NovaSession } from "@/types"

const SidebarFragment = graphql(`
  fragment Sidebar on User {
    id
    ...VerticalNav
  }
`)

interface Props {
  serverSession: NovaSession
  user: FragmentType<typeof SidebarFragment> | null | undefined
}

// Collapsed paths are all paths starting with the path, exact are only the exact path
const creativeCollapsedPaths = ["/hire/", "/messages", "/jobs/applications"]
const creativeCollapsedExactPaths = ["/", "/jobs"]

const clientCollapsedPaths = ["/hire/", "/messages"]
const clientCollapsedExactPaths = [] as string[]

export const Sidebar: React.FC<Props> = props => {
  const { serverSession } = props
  const { secondaryBar } = useSidebar()
  const isCreative = serverSession?.account === "creative"
  // This query is fetching NotificationsNavItem data which is being read from cache in the nav item
  const pathname = usePathname()
  const collapsedPaths = isCreative ? creativeCollapsedPaths : clientCollapsedPaths
  const exactCollapsedPaths = isCreative ? creativeCollapsedExactPaths : clientCollapsedExactPaths
  const pathnameShouldCollapsed = collapsedPaths.some(testPath => pathname?.startsWith(testPath))
  const exactPathnameShouldCollapsed = exactCollapsedPaths.some(testPath => pathname === testPath)
  const forceCollapse = secondaryBar.open || pathnameShouldCollapsed || exactPathnameShouldCollapsed
  const user = useFragment(SidebarFragment, props.user)

  return (
    <div
      className={clsx(
        "fixed hidden h-screen w-[96px] flex-shrink-0 flex-grow-0 md:relative md:order-1 md:block lg:w-[224px]",
        forceCollapse && "!w-[96px]"
      )}>
      <div
        style={{
          zIndex: 100,
        }}
        className={clsx(
          "transition-width fixed bottom-0 left-0 top-0 hidden w-[96px] flex-col border-r border-bg-3 bg-bg-1 px-4 py-9 duration-200 ease-in-out md:flex lg:w-[224px]",
          forceCollapse && "!w-[96px]"
        )}>
        <VerticalNav serverSession={serverSession} forceHideLabels={forceCollapse} user={user} />
      </div>
      <SecondarySidebar serverSession={serverSession} />
    </div>
  )
}
